/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

// src/api/axiosInstance.js
import axios from "axios";
import {
  notifyCrossTabDataChanged,
  shouldBroadcastAxiosDataChange,
} from "../utils/crossTabDataSync";
import {
  readStoredDisplayCurrency,
  readStoredFxRateSetId,
  resolveRequestBusinessUnitId,
} from "../currency/currencyStorage";
import { applyDemoAxiosAdapter, installDemoFetchGuard } from "../demo/demoApi";
import { isDemoMode, readDemoToken, readDemoUser } from "../demo/demoMode";
import { endRevokedBrowserSession } from "../utils/sessionRevocation";



const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

const DATA_SYNC_INTERCEPTOR_FLAG = "__palantirCrossTabDataSyncInstalled";

function broadcastSuccessfulMutation(response) {
  if (!response || response.status < 200 || response.status >= 400) return;
  if (!shouldBroadcastAxiosDataChange(response.config)) return;

  notifyCrossTabDataChanged({
    method: response.config?.method,
    url: response.config?.url,
    status: response.status,
  });
}

function installDataSyncResponseInterceptor(client) {
  if (!client?.interceptors?.response || client[DATA_SYNC_INTERCEPTOR_FLAG]) return;

  try {
    Object.defineProperty(client, DATA_SYNC_INTERCEPTOR_FLAG, {
      value: true,
      configurable: true,
    });
  } catch (_error) {
    client[DATA_SYNC_INTERCEPTOR_FLAG] = true;
  }

  client.interceptors.response.use(
    (response) => {
      broadcastSuccessfulMutation(response);
      return response;
    },
    (error) => Promise.reject(error)
  );
}

installDataSyncResponseInterceptor(axios);
installDataSyncResponseInterceptor(axiosInstance);
installDemoFetchGuard();

function tokenScope(token) {
  try {
    const segments = String(token || "").split(".");
    if (segments.length !== 3 || !segments.every(Boolean)) return null;
    const encoded = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=")));
    return payload;
  } catch (_error) { return null; }
}

// 🔐 Interceptor para añadir token
axiosInstance.interceptors.request.use((config) => {
  const isPosDeviceRequest =
    config.url?.includes("/api/pos/device-pairing/") ||
    config.url?.includes("/api/pos/guest-device/") ||
    config.url?.includes("/api/pos/service-display/");
  if (config.url?.includes("/api/auth/login") || isPosDeviceRequest) {
    return config;
  }

  const demo = isDemoMode();
  const token = demo ? readDemoToken() : localStorage.getItem("palantir_token");
  const expectedUnit = config.headers.get?.("X-Pilcore-Business-Unit-Id") ?? config.headers["X-Pilcore-Business-Unit-Id"];
  if (expectedUnit !== undefined && expectedUnit !== null) {
    const scope = demo ? readDemoUser() : tokenScope(token);
    const expectedUserId = config.pilcoreExpectedUserId;
    if (!/^[1-9]\d*$/.test(String(expectedUnit)) || !Number.isSafeInteger(Number(expectedUnit)) || Number(scope?.active_business_unit_id) !== Number(expectedUnit) ||
      (expectedUserId != null && String(scope?.id) !== String(expectedUserId))) {
      const code = config.url?.includes("/whatsapp-delivery-notes/senders") ? "WHATSAPP_BUSINESS_UNIT_CHANGED" : "OCR_BUSINESS_UNIT_CHANGED";
      return Promise.reject(Object.assign(new Error("The session business unit changed before this request could be sent."), {
        code, config, response: { status: 409, data: { code }, config },
      }));
    }
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const businessUnitId = resolveRequestBusinessUnitId(config);
  const displayCurrency = readStoredDisplayCurrency(businessUnitId);
  const fxRateSetId = readStoredFxRateSetId(businessUnitId);
  if (displayCurrency) config.headers["X-Display-Currency"] = displayCurrency;
  if (fxRateSetId) config.headers["X-Fx-Rate-Set-Id"] = fxRateSetId;

  return config;
});

axios.interceptors.request.use(applyDemoAxiosAdapter);
axiosInstance.interceptors.request.use(applyDemoAxiosAdapter);

const SESSION_RESPONSE_INTERCEPTOR_FLAG = "__palantirSessionResponseInterceptorInstalled";

function installSessionResponseInterceptor(client) {
  if (!client?.interceptors?.response || client[SESSION_RESPONSE_INTERCEPTOR_FLAG]) return;

  try {
    Object.defineProperty(client, SESSION_RESPONSE_INTERCEPTOR_FLAG, {
      value: true,
      configurable: true,
    });
  } catch (_error) {
    client[SESSION_RESPONSE_INTERCEPTOR_FLAG] = true;
  }

  client.interceptors.response.use(
    response => response,
    error => {
      if (endRevokedBrowserSession(error)) {
        console.warn("🔒 Token inválido o expirado. Redirigiendo a login...");
      }

      return Promise.reject(error);
    }
  );
}

// Capture revoked/expired sessions for both the shared API client and legacy
// calls that still import axios directly.
installSessionResponseInterceptor(axios);
installSessionResponseInterceptor(axiosInstance);


export default axiosInstance;
